import React, { useContext, useEffect, useMemo } from 'react';
import { parse } from 'query-string';
import { Box, Row } from '../../../SubComponents/Modals';
import { Label } from '../../../SubComponents/Text';
import styled from 'styled-components/macro';
import Loading from '../../../SubComponents/Loading';
import useFormatPrice from '../../../Hooks/use-format-price';
import DumbDurationOption from './dumb-duration-option';
import CustomGiftCardAmountInput from './custom-gift-card-amount-input';
import useTypeSafeLocalize from '../../../Hooks/use-type-safe-localize';
import { LocalizationCategory, ProductLocalization } from '../../../localizations/types';
import { giftcardState } from '../client-state/giftcard-state-context';
import { getDurationNumber } from '../../../Utils/utils';
import { GiftcardCustomAmountReducerActionTypes } from '../../../redusers/giftcard-custom-amount-reducer';
import useGiftcardMinimalShop from '../server-data/use-giftcard-minimal-shop';
import useGiftcardShop from '../server-data/use-giftcard-shop';
import useGiftcardMinimalShops from '../server-data/use-giftcard-minimal-shops';
import { giftcardReducerActionTypes } from '../../../redusers/giftcardReducer';

const OptionsContainer = styled(Row)`
  overflow-x: auto;
  &:before,
  &:after {
    content: '';
  }
`;

const defaultDurationOptions = [{ duration: '-', price: '-' }];
const initialDuration = Number(parse(window.location.search).duration);

interface OptionType {
  duration: number | string;
  price: string;
}

const GiftCardAmount = () => {
  const { isFetching: isFetchingShops } = useGiftcardMinimalShops();
  const { data: minimalShop, isFetching: isFetchingShop } = useGiftcardMinimalShop();
  const { data: shop } = useGiftcardShop();
  const { state, dispatch } = useContext(giftcardState);
  const localize = useTypeSafeLocalize();
  const formatPrice = useFormatPrice();

  useEffect(() => {
    if (minimalShop?.defaultGiftcardDuration && !initialDuration) {
      console.log(
        'dispatching UPDATE_DURATION, default giftcard duration:',
        minimalShop.defaultGiftcardDuration
      );
      dispatch({
        type: giftcardReducerActionTypes.UPDATE_DURATION,
        payload: getDurationNumber(minimalShop.defaultGiftcardDuration),
      });
    }
  }, [dispatch, minimalShop?.defaultGiftcardDuration]);

  const options: OptionType[] = useMemo(() => {
    if (
      minimalShop?.giftcardDurationOptions &&
      minimalShop.giftcardDurationOptions.length > 0
    ) {
      return minimalShop.giftcardDurationOptions
        .map((option) => {
          return {
            duration: getDurationNumber(option.duration),
            price: formatPrice(option.price),
          };
        })
        .sort((a, b) => a.duration - b.duration);
    }

    return defaultDurationOptions;
  }, [minimalShop?.giftcardDurationOptions, formatPrice]);

  if (shop?.configuration?.enableCustomAmountGiftCards) {
    options.push({
      duration: 0,
      price: localize(LocalizationCategory.product, ProductLocalization.custom),
    });
  }

  const customAmount =
    shop?.configuration?.enableCustomAmountGiftCards && state.giftcard.duration === 0;

  return (
    <div>
      <Box flexDirection="column" p="0" mb="4" width="100%">
        <Label px="12px" lineHeight="35px">
          {shop?.configuration?.hideGiftCardDurations
            ? localize(LocalizationCategory.product, ProductLocalization.amount)
            : localize(LocalizationCategory.product, ProductLocalization.durationInHours)}
        </Label>

        <Loading loading={isFetchingShops || isFetchingShop}>
          <OptionsContainer justifyContent="space-between">
            {options.map((option) => {
              return <DumbDurationOption {...option} key={option.duration} />;
            })}
          </OptionsContainer>
        </Loading>
      </Box>

      {customAmount ? (
        <CustomGiftCardAmountInput
          key={state.giftcard.duration}
          value={state.customAmount}
          onChange={(value) =>
            dispatch({
              type: GiftcardCustomAmountReducerActionTypes.CUSTOM_AMOUNT_UPDATE,
              payload: value,
            })
          }
        />
      ) : null}
    </div>
  );
};

export default GiftCardAmount;
